import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CircleNotch, Microphone, Stop } from "@phosphor-icons/react";
import { http } from "@/lib/api";

const MAX_SECONDS = 120; // keep voice notes short — backend caps at whisper-1's 25MB

/** Voice note recorder — record a spoken observation, transcribe it with
 * Whisper, and hand the text back for editing before submit. */
export default function VoiceNoteButton({ onTranscript, testid = "voice-note" }) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const [seconds, setSeconds] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const cleanup = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  };

  useEffect(() => () => {
    cleanup();
    try { recRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
  }, []);

  const stopRecording = () => {
    cleanup();
    try { recRef.current?.stop(); } catch { setState("idle"); }
  };

  // Two-minute cap
  useEffect(() => {
    if (state === "recording" && seconds >= MAX_SECONDS) {
      toast("Two-minute voice limit — stopping the recording");
      stopRecording();
    }
  }, [seconds, state]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size < 500) {
          setState("idle");
          toast.error("Recording too short — hold the button and speak");
          return;
        }
        setState("transcribing");
        try {
          const fd = new FormData();
          fd.append("file", blob, `voice.${mime.includes("webm") ? "webm" : "mp4"}`);
          const { data } = await http.post("/nature/voice/transcribe", fd, { timeout: 90000 });
          if (data?.text) {
            onTranscript?.(data.text);
            toast.success("Voice note added — edit the text below before submitting");
          } else {
            toast.error("Nothing heard — try again closer to the microphone");
          }
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Couldn't transcribe — please type instead");
        } finally {
          setState("idle");
        }
      };
      recRef.current = rec;
      rec.start();
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      setState("recording");
    } catch {
      toast.error("Microphone unavailable — allow mic access in your browser");
    }
  };

  if (state === "transcribing") {
    return (
      <span data-testid={`${testid}-transcribing`}
        className="inline-flex items-center gap-1.5 rounded-full border border-black/15 px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)]">
        <CircleNotch size={13} weight="bold" className="animate-spin" /> Transcribing…
      </span>
    );
  }

  if (state === "recording") {
    return (
      <button type="button" onClick={stopRecording} data-testid={`${testid}-recording`}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#7C2D12] text-[#F5EBDC] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase animate-pulse">
        <span className="w-2 h-2 rounded-full bg-[#F5EBDC]" />
        {String(Math.floor(seconds / 60)).padStart(1, "0")}:{String(seconds % 60).padStart(2, "0")}
        <Stop size={13} weight="fill" /> Stop
      </button>
    );
  }

  return (
    <button type="button" onClick={start} data-testid={`${testid}-button`}
      title="Speak instead of typing — you'll edit the text before it's submitted"
      className="inline-flex items-center gap-1.5 rounded-full border border-[#1B4332]/30 bg-[#1B4332]/8 text-[#1B4332] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-[#1B4332] hover:text-[#F5EBDC] transition-colors">
      <Microphone size={13} weight="bold" /> Speak it
    </button>
  );
}
